import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Header } from 'semantic-ui-react';
import LoaderComponent from 'components/LoaderComponent';
import styled from 'styled-components';
import { numberFormatter } from 'utils/fomattingUtils';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import moment from 'moment';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { FormApi } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import {
  OpenItemListData,
  OpenItemListPrintRequest,
  OpenItemListPrintResponse,
  OpenItemListSearchResponse
} from 'ts-types/api.types';
import ErrorMessage from 'components/final-form/ErrorMessage';
import { BillJournalOrderBy, BillReceiver } from 'ts-types/api.enums';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { RouteComponentProps } from 'react-router';
import { OpenItemListFormData } from 'components/billing/open-item-list/OpenItemListView';
import { exportOpenItemList, getOpenItemListPdfUrl, getOpenItemListPrintPreview } from 'services/openListItemServices';
import PdfModal from 'components/pdf-modal/PdfModal';
import SearchResultComponent from 'components/SearchResultComponent';
import { SortDirection, SortDirectionType, SortIndicator } from 'react-virtualized';

const StyledTable = styled(VirtualizedTable)`

  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row.totalRow {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledTotalsRowTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    display: none;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__headerRow.headerRow {
    background-color: rgba(0, 0, 0, .2);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: none;
    padding-top: 0.85rem;
    padding-right: 1rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: none;
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const BillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
  min-height: 220px;
`;

const TotalsContainer = styled.div`
  position: relative;
  min-height: 46px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: column;
  height: 100%;

  .bill-list-form {
    display: flex;
    flex-direction: column;
  }
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

const StyledPatientId = styled.span`
  color: #757575;
`;

const StyledFormButtonsRow = styled(FormButtonsRow)`
  margin-top: 1rem;
`;

const StyledHeader = styled(Header)`
  float: right;
  margin-top: .5rem !important;
`;

const SortableColumnHeader = styled.div`
  cursor: pointer;
`;

const SortDirectionContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  openItemListData: OpenItemListData,
  billsLoading: boolean,
  formData: OpenItemListFormData,
  sortBy: string,
  sortDirection: SortDirectionType,
  sort: (sortBy: string, sortDirection: SortDirectionType) => void,
  setErrorMessage: (errorMessage?: string) => void
}

interface State {
  openItemListPdfData?: OpenItemListPrintResponse,
  openItemListPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource
}

class OpenItemListTableSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      openItemListPdfModalOpen: false,
      cancelTokenSource: cancelTokenSource
    };
  }

  getPdfUrl = (openItemListResponse: OpenItemListPrintResponse) => {
    return getOpenItemListPdfUrl(openItemListResponse.pdfId, openItemListResponse.pdfName);
  }

  openOpenItemListPdfModal = (openItemListResponse: OpenItemListPrintResponse) => {
    this.setState({
      openItemListPdfModalOpen: true,
      openItemListPdfData: openItemListResponse
    })
  };

  closeOpenItemListPdfModal = () => {

    this.setState({
      openItemListPdfModalOpen: false,
      openItemListPdfData: undefined
    });
  };

  dateCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return (
          <div>{moment(cellData).format("DD.MM.YYYY")}</div>
      );
    }

    return "";
  }

  amountCellRenderer = ({rowData}: any) => {
    return (
        <StyledCell>{numberFormatter.format(rowData.amount)}</StyledCell>
    );
  }

  paidAmountCellRenderer = ({rowData}: any) => {
    return (
        <StyledCell>{numberFormatter.format(rowData.paidAmount)}</StyledCell>
    );
  }

  openAmountCellRenderer = ({rowData}: any) => {
    return <StyledCell>{numberFormatter.format(rowData.openAmount)}</StyledCell>
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData && cellData.length > 0) {
      return <div>{cellData}. {getMessage("bill.reminder")}</div>
    }
    return "";
  };

  overdueCellRenderer = ({cellData}: any) => {
    if (cellData) {
      const {getMessage} = this.props;
      return (
          <div>{cellData} {getMessage("days")}</div>
      );
    }

    return "";
  };

  billsRowGetter = (bills: Array<OpenItemListSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billReceiverCellRenderer = ({rowData, cellData}: any) => {
    if (cellData === BillReceiver.PATIENT) {
      const patientDisplay = `${rowData.patientFullName}, ${rowData.patientCity}`;
      return (
          <div title={patientDisplay}>{patientDisplay}</div>
      );
    }
    return (
        <div>{`${rowData.guarantorDescription}`}</div>
    );
  };

  patientCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const patientDisplay = `${rowData.patientId} - ${rowData.patientFullName}`;
    if (cellData) {
      return (
          <div title={patientDisplay}><StyledPatientId>{rowData.patientId}</StyledPatientId> - {rowData.patientFullName}
          </div>
      );
    }

    return getMessage("billJournal.various");
  };

  billsRowRenderer = ({className, columns, index, key, style}: any) => {

    const {openItemListData: {bills}} = this.props;
    const numberOfBills = bills.length;

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    const totalIx = index + 1;

    if (totalIx > numberOfBills) {

      const noBorderFieldsIndexes = [0, 1, 2, 3];

      className = className + " totalRow";

      return (
          <div
              {...a11yProps}
              className={className}
              key={key}
              role="row"
              style={styles}
          >
            {
              columns.map((column: any, index: number) => {
                let editColumn = {...column, props: {...column.props}};
                if (noBorderFieldsIndexes.includes(index)) {
                  editColumn.props.className = `${editColumn.props.className}noBorder`;
                  return editColumn;
                }
                return editColumn;
              })
            }
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    );
  };

  handleSubmit = async (values: Partial<OpenItemListFormData>) => {

    const {bills} = values;
    const {cancelTokenSource} = this.state;
    const {setErrorMessage, getMessage} = this.props;

    if (bills && bills.length) {
      const billIds: Array<number> = bills.map(bill => bill.id);

      const openItemListPrintRequest: Partial<OpenItemListPrintRequest> = {
        mandateId: values.mandateId,
        patientId: values.patientId,
        date: values.date,
        orderBy: values.orderBy,
        billIds: billIds
      }

      await getOpenItemListPrintPreview(openItemListPrintRequest, cancelTokenSource)
          .then(response => {
            this.openOpenItemListPdfModal(response);

          })
          .catch((error) => setErrorMessage(getMessage("error.general")));
    }
  };

  exportData = async (values: any, bills: Array<OpenItemListSearchResponse>) => {
    const {cancelTokenSource} = this.state;
    const {setErrorMessage, getMessage} = this.props;

    if (bills && bills.length) {
      const billIds: Array<number> = bills.map(bill => bill.id);

      const openItemListPrintRequest: Partial<OpenItemListPrintRequest> = {
        mandateId: values.mandateId,
        patientId: values.patientId,
        date: values.date,
        orderBy: values.orderBy,
        billIds: billIds
      }

      await exportOpenItemList(openItemListPrintRequest, cancelTokenSource)
          .catch((error) => setErrorMessage(getMessage("error.general")));
    }
  }

  render(): React.ReactNode {
    const {openItemListPdfModalOpen, openItemListPdfData} = this.state;
    const {formData} = this.props;

    return (
        <React.Fragment>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
          {
            openItemListPdfModalOpen &&
            <PdfModal
                modalOpen={openItemListPdfModalOpen}
                onCloseModal={this.closeOpenItemListPdfModal}
                pdfResponses={[openItemListPdfData]}
                getPdfUrl={this.getPdfUrl}
            />
          }
        </React.Fragment>
    )
  };

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {bills} = values;
    const {openItemListData: {totalOpenAmount}, billsLoading, getMessage} = this.props;

    const countBills = bills.length;
    const emptyBillArray = countBills < 1;

    const formatTotalOpenAmount = numberFormatter.format(totalOpenAmount);
    const formatDate = moment(values.date).format("DD.MM.YYYY");

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent className="bill-list-form">
            <Field
                name="error"
                component={ErrorMessage}
            />
            <StyledFormContent>
              <StyledContainer style={{width: "100%"}}>
                <Header>
                  <SearchResultComponent numResults={countBills} textMessage={"header.navigation.billJournalResults"}/>
                </Header>
                <BillsContainer>
                  {
                    billsLoading
                        ? <LoaderComponent
                            message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                        : <React.Fragment>
                          {this.renderBillsTable(form, values, bills)}
                        </React.Fragment>
                  }
                </BillsContainer>

                {
                  !emptyBillArray &&
                  <React.Fragment>
                    <TotalsContainer>
                      {this.renderTotalsRowTable()}
                    </TotalsContainer>
                    <StyledHeader>
                      {getMessage("openItemList.openAmount")} &nbsp;&nbsp; {formatDate} &nbsp;&nbsp;
                      {formatTotalOpenAmount}
                    </StyledHeader>
                  </React.Fragment>
                }
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
          <StyledFormButtonsRow>
            <Button compact type="submit" primary disabled={submitting || emptyBillArray}>
              {getMessage("output")}
            </Button>
            <Button compact type="button" primary disabled={submitting || emptyBillArray}
                    onClick={() => this.exportData(values, bills)}>
              {getMessage("bill.export.button")}
            </Button>
          </StyledFormButtonsRow>
        </StyledForm>
    );
  };

  renderBillsTable = (form: FormApi, values: any, bills: Array<OpenItemListSearchResponse>): React.ReactNode => {

    const {sortBy, sortDirection, sort, getMessage} = this.props;

    const rowCount = bills.length;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer}
            rowCount={bills.length > 0 ? rowCount : 0}
            sortBy={sortBy}
            sortDirection={sortDirection}
            sort={sort}
            columns={[
              {
                width: 100,
                label: getMessage("billJournal.billNumber"),
                dataKey: "id"
              },
              {
                width: 400,
                label: getMessage("billJournal.billReceiver"),
                dataKey: "billReceiver",
                cellRenderer: this.billReceiverCellRenderer
              },
              {
                width: 400,
                label: getMessage("patient"),
                dataKey: "patientId",
                cellRenderer: this.patientCellRenderer
              },
              {
                width: 200,
                label: getMessage("date"),
                dataKey: "date",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 200,
                label: getMessage("dueDate"),
                dataKey: "dueDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 120,
                label: getMessage("overdue"),
                dataKey: "overdue",
                cellRenderer: this.overdueCellRenderer
              },
              {
                width: 200,
                label: getMessage("bill.amountHeader"),
                dataKey: "amount",
                cellRenderer: (data: any) => this.amountCellRenderer(data)
              },
              {
                width: 200,
                label: getMessage("billJournal.paid"),
                dataKey: "paidAmount",
                cellRenderer: (data: any) => this.paidAmountCellRenderer(data)
              },
              {
                width: 200,
                label: getMessage("bill.status.open"),
                dataKey: "amount",
                cellRenderer: (data: any) => this.openAmountCellRenderer(data)
              },
              {
                width: 200,
                label: getMessage("status"),
                dataKey: "status",
                cellRenderer: this.statusCellRenderer
              }
            ]}
        />
    )
  };

  headerRowRenderer = ({className, columns, style}: any) => {
    return (
        <StyledCell
            className={className}
            role="row"
            style={style}
        >
          {columns}
        </StyledCell>
    )
  };

  renderTotalsRowTable = (): React.ReactNode => {

    const {openItemListData} = this.props;

    return (
        <StyledTotalsRowTable
            headerRowRenderer={this.headerRowRenderer}
            rowGetter={this.billsRowGetter([])}
            rowRenderer={this.billsRowRenderer}
            rowCount={0}
            columns={[
              {
                width: 100,
                label: "",
                dataKey: "id"
              },
              {
                width: 400,
                label: "",
                dataKey: "billReceiver"
              },
              {
                width: 400,
                label: "",
                dataKey: "patientId"
              },
              {
                width: 200,
                label: "",
                dataKey: "date"
              },
              {
                width: 200,
                label: "",
                dataKey: "dueDate"
              },
              {
                width: 120,
                label: "",
                dataKey: "overdue"
              },
              {
                width: 200,
                label: numberFormatter.format(openItemListData.totalAmount),
                dataKey: "amount"
              },
              {
                width: 200,
                label: numberFormatter.format(openItemListData.totalPaidAmount),
                dataKey: "paidAmount"
              },
              {
                width: 200,
                label: numberFormatter.format(openItemListData.totalOpenAmount),
                dataKey: "openAmount"
              },
              {
                width: 200,
                label: "",
                dataKey: "status"
              }
            ]}
        />
    )
  };
}

export default withAuthContext(withRouterWorkaround(withMessages(OpenItemListTableSegment)));
